/* ------------------------------------------------------------------
   JOUBERT — la coupe, morphose liquide.

   Le maillot devient le sac : un front avance, déformé par un bruit
   animé, et les couleurs se dispersent à son passage comme à travers
   un liquide. Écrit à la main, sans bibliothèque.

   Les deux vraies images restent dans la page sous le rendu : le
   référencement et la synthèse vocale les voient. Si le navigateur
   refuse WebGL, si le contexte est perdu, ou si le système demande
   moins d'animation, on laisse simplement la version en CSS.
   ------------------------------------------------------------------ */
(() => {
  const doux = matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (doux) return;

  const SOMMET = `
attribute vec2 p;
varying vec2 vUv;
void main(){ vUv = p * 0.5 + 0.5; gl_Position = vec4(p, 0.0, 1.0); }`;

  const FRAGMENT = `
precision highp float;
varying vec2 vUv;
uniform sampler2D uA, uB;
uniform float uProgres, uTemps, uRatio, uEtendue, uZoom;

vec3 mod289(vec3 x){ return x - floor(x * (1.0/289.0)) * 289.0; }
vec2 mod289(vec2 x){ return x - floor(x * (1.0/289.0)) * 289.0; }
vec3 permute(vec3 x){ return mod289(((x*34.0)+1.0)*x); }
float bruit(vec2 v){
  const vec4 C = vec4(0.211324865, 0.366025403, -0.577350269, 0.024390243);
  vec2 i = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0,0.0) : vec2(0.0,1.0);
  vec4 x12 = x0.xyxy + C.xxzz; x12.xy -= i1;
  i = mod289(i);
  vec3 pp = permute( permute( i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
  m = m*m; m = m*m;
  vec3 x = 2.0 * fract(pp * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0*a0 + h*h);
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

/* la scene est carree : on l'inscrit dans le cadre sans la deformer */
vec2 cadrer(vec2 uv, float k){
  vec2 c = uv - 0.5;
  if (uRatio > 1.0) c.x *= uRatio; else c.y /= uRatio;
  return c * k + 0.5;
}

/* une ombre douce, posee sous l'objet ; elle se resserre quand il monte */
float ombre(vec2 uv, float leve){
  vec2 c = uv - vec2(0.5, 0.085);
  if (uRatio > 1.0) c.x *= uRatio; else c.y /= uRatio;
  c.x /= 0.34 - leve * 0.06;
  c.y /= 0.026 - leve * 0.005;
  return (1.0 - smoothstep(0.30, 1.0, length(c))) * (0.17 - leve * 0.06);
}

void main(){
  vec2 uv = vUv;

  float n1 = bruit(vec2(uv.x * 1.6 * uRatio, uv.y * 2.2 + uTemps * 0.05));
  float n2 = bruit(vec2(uv.x * 5.0 * uRatio, uv.y * 6.5 - uTemps * 0.08));
  float n = n1 * 0.72 + n2 * 0.28;

  /* le front traverse l objet, pas le cadre : sinon la moitie du
     mouvement se joue sur du blanc */
  /* la coupe part de la droite et descend vers la gauche, comme le
     faisait le CSS : le maillot reste a gauche du front, le sac a droite */
  float marge = uEtendue + 0.10;
  float front = mix(0.5 + marge, 0.5 - marge, uProgres);
  float d = front - uv.x + n * 0.13;
  float melange = smoothstep(-0.05, 0.05, d);
  float bord = exp(-abs(d) * 15.0);

  vec2 dec = vec2(n * 0.9, bruit(vec2(uv.y * 4.0, uv.x * 4.0 * uRatio + uTemps * 0.07)) * 0.6) * bord * 0.042;

  /* levitation : chacun respire a son rythme, comme dans la version CSS */
  float leveA = sin(uTemps * 0.55) * 0.5 + 0.5;
  float leveB = sin(uTemps * 0.47 + 1.9) * 0.5 + 0.5;
  vec2 uvA = cadrer(uv + dec + vec2(0.0, (leveA - 0.5) * 0.045), uZoom);
  vec2 uvB = cadrer(uv - dec + vec2(0.0, (leveB - 0.5) * 0.040), uZoom);

  float disp = bord * 0.006;
  vec4 a, b;
  a.r = texture2D(uA, uvA + vec2(disp, 0.0)).r;
  a.g = texture2D(uA, uvA).g;
  a.b = texture2D(uA, uvA - vec2(disp, 0.0)).b;
  b.r = texture2D(uB, uvB + vec2(disp, 0.0)).r;
  b.g = texture2D(uB, uvB).g;
  b.b = texture2D(uB, uvB - vec2(disp, 0.0)).b;

  vec3 c = mix(b.rgb, a.rgb, melange);
  c -= ombre(uv, mix(leveB, leveA, melange));
  c += exp(-abs(d) * 46.0) * 0.16;

  gl_FragColor = vec4(c, 1.0);
}`;

  document.querySelectorAll('[data-jb-coupe]').forEach((coupe) => {
    const avant = coupe.querySelector('.jb-coupe-avant img');
    const apres = coupe.querySelector('.jb-coupe-apres img');
    if (!avant || !apres) return;
    /* Le fondu liquide ne se lance que si la section le demande. Sinon on
       laisse le rendu en CSS : les deux images se croisent, séparées par la
       barre blanche, qui suit le défilement et le doigt. */
    if (coupe.dataset.jbMorphose !== 'oui') return;

    const toile = document.createElement('canvas');
    toile.className = 'jb-coupe-toile';
    toile.setAttribute('aria-hidden', 'true');

    let gl;
    try {
      gl = toile.getContext('webgl', { antialias: false, alpha: false, powerPreference: 'low-power' });
    } catch (e) { return; }
    if (!gl) return;

    const compiler = (type, src) => {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null;
    };
    const vs = compiler(gl.VERTEX_SHADER, SOMMET);
    const fs = compiler(gl.FRAGMENT_SHADER, FRAGMENT);
    if (!vs || !fs) return;

    const prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
    gl.useProgram(prog);

    const tampon = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, tampon);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'p');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    const uA = gl.getUniformLocation(prog, 'uA');
    const uB = gl.getUniformLocation(prog, 'uB');
    const uProgres = gl.getUniformLocation(prog, 'uProgres');
    const uTemps = gl.getUniformLocation(prog, 'uTemps');
    const uRatio = gl.getUniformLocation(prog, 'uRatio');
    const uEtendue = gl.getUniformLocation(prog, 'uEtendue');
    const uZoom = gl.getUniformLocation(prog, 'uZoom');

    let etendue = 0.2;
    const scene = (img, hauteur) => {
      const c = document.createElement('canvas');
      c.width = c.height = 1024;
      const x = c.getContext('2d');
      x.fillStyle = '#FFFFFF';
      x.fillRect(0, 0, c.width, c.height);
      const h = c.height * hauteur;
      const l = img.naturalWidth / img.naturalHeight * h;
      x.drawImage(img, (c.width - l) / 2, (c.height - h) / 2 - c.height * 0.03, l, h);
      etendue = Math.max(etendue, l / c.width / 2);
      return c;
    };

    const texture = (source) => {
      const t = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
      return t;
    };

    const attendre = (img) => (img.complete && img.naturalWidth
      ? Promise.resolve()
      : new Promise((r) => img.addEventListener('load', r, { once: true })));

    const demarrer = async () => {
      await Promise.all([attendre(avant), attendre(apres)]);
      let tA, tB;
      try {
        tA = texture(scene(avant, 0.74));
        tB = texture(scene(apres, 0.62));
      } catch (e) {
        // image d'une autre origine sans en-tête d'autorisation : on garde le CSS
        return;
      }
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, tA); gl.uniform1i(uA, 0);
      gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, tB); gl.uniform1i(uB, 1);

      coupe.appendChild(toile);
      coupe.classList.add('jb-coupe--gl');

      const dimension = () => {
        const r = Math.min(devicePixelRatio || 1, 1.75);
        toile.width = Math.max(1, Math.round(toile.clientWidth * r));
        toile.height = Math.max(1, Math.round(toile.clientHeight * r));
        gl.viewport(0, 0, toile.width, toile.height);
        const ratio = toile.width / toile.height;
        /* sur un cadre etroit la scene carree est inscrite par la largeur :
           les objets s y perdent. On resserre le cadrage pour les agrandir. */
        const zoom = ratio >= 1.3 ? 0.84 : 0.70;
        gl.uniform1f(uRatio, ratio);
        gl.uniform1f(uZoom, zoom);
        gl.uniform1f(uEtendue, (ratio > 1.0 ? etendue / ratio : etendue) / zoom);
      };
      addEventListener('resize', dimension);
      dimension();

      /* on ne dessine qu'à l'écran : ailleurs, rien ne tourne */
      let visible = false;
      new IntersectionObserver(([e]) => { visible = e.isIntersecting; if (visible) boucle(); }, { rootMargin: '150px' }).observe(coupe);

      const t0 = performance.now();
      let demande = 0;
      const dessiner = () => {
        demande = 0;
        if (!visible) return;
        const cible = parseFloat(getComputedStyle(coupe).getPropertyValue('--x')) / 100 || 0;
        gl.uniform1f(uProgres, cible);
        gl.uniform1f(uTemps, (performance.now() - t0) / 1000);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        boucle();
      };
      const boucle = () => { if (!demande && visible) demande = requestAnimationFrame(dessiner); };
      boucle();

      toile.addEventListener('webglcontextlost', (e) => {
        e.preventDefault();
        visible = false;
        coupe.classList.remove('jb-coupe--gl');
        toile.remove();
      });
    };

    demarrer();
  });
})();
